import type { ReactNode } from 'react'
import type { FeatureKey } from '@academy/config'
import { useFeatures } from '../lib/features.js'
import { Empty } from './Empty.js'
import { Skeleton } from './Skeleton.js'

interface FeatureGateProps {
  feature: FeatureKey
  children: ReactNode
}

/**
 * Renders children only if the academy's plan enables `feature`. Otherwise shows
 * a calm "not available on your plan" state. Use this to guard whole pages for
 * Phase-2 modules so a deep link does not render a screen that 403s on every
 * API call. The nav already hides the entry points; this catches direct URLs.
 */
export function FeatureGate({ feature, children }: FeatureGateProps) {
  const { features, isLoading } = useFeatures()

  if (isLoading) {
    return <Skeleton className="h-40 w-full" />
  }

  if (!features[feature]) {
    return (
      <Empty
        icon="alert"
        title="Not available on your plan"
        description="This feature is not part of your current plan. Contact your administrator to enable it."
      />
    )
  }

  return <>{children}</>
}
